'use client';

import { createElement } from 'react';
import {
  CableIcon,
  Copy01Icon,
  RadioIcon,
  ReplayIcon,
  Wifi01Icon,
} from '@terra/ui-icons';
import { z } from 'zod';
import type { CustomAction } from '../tool-config-types';

/**
 * Extract HTTP request data from tool input/output in the format expected by replay.
 * Handles multiple formats:
 * - http-tool: input.url, input.options.{method, headers, body}
 * - validated-http-tool: output.request.{url, method}
 */
const extractHttpRequestData = (
  input: unknown,
  output: unknown,
): {
  method: string;
  url: string;
  headers?: Record<string, string>;
  body?: string;
  bodyEncoding?: 'utf8' | 'base64';
} | null => {
  // Try http-tool format first (url in input)
  if (typeof input === 'object' && input !== null) {
    const inputObj = input as Record<string, unknown>;

    if (typeof inputObj.url === 'string') {
      // http-tool format: input.url with input.options.{method, headers, body}
      // Some variations may have headers/body/method at root level
      const options = inputObj.options as Record<string, unknown> | undefined;
      // Try options first, then root level
      const method =
        (options?.method as string) || (inputObj.method as string) || 'GET';
      const headers =
        (options?.headers as Record<string, string>) ||
        (inputObj.headers as Record<string, string>) ||
        undefined;
      const body =
        (options?.body as string) ||
        (options?.formData as string) ||
        (inputObj.body as string) ||
        undefined;

      return {
        method: method.toUpperCase(),
        url: inputObj.url,
        headers,
        body,
      };
    }
  }

  // Try validated-http-tool format (url in output.request)
  if (typeof output === 'object' && output !== null) {
    const outputObj = output as Record<string, unknown>;
    const request = outputObj.request as Record<string, unknown> | undefined;

    if (request && typeof request.url === 'string') {
      // Extract headers and body from validated-http format
      const headers = request.headers as Record<string, string> | undefined;
      const body =
        typeof request.body === 'string'
          ? request.body
          : typeof request.body === 'object' && request.body !== null
            ? JSON.stringify(request.body)
            : undefined;

      return {
        method: ((request.method as string) || 'GET').toUpperCase(),
        url: request.url,
        headers,
        body,
      };
    }
  }

  return null;
};

export const getHttpCustomActions = (ctx: {
  input: unknown;
  output: unknown;
  isCompleted: boolean;
}): CustomAction[] => {
  // Need completed status to enable action
  if (!ctx.isCompleted) return [];
  // Extract HTTP request data from input or output
  const httpRequestData = extractHttpRequestData(ctx.input, ctx.output);

  if (!httpRequestData) return [];

  return [
    {
      icon: createElement(ReplayIcon, { size: 11 }),
      label: 'Send to Replay',
      onClick: () => {
        window.dispatchEvent(
          new CustomEvent('terra-replay-http-request', {
            detail: httpRequestData,
            bubbles: true,
          }),
        );
      },
    },
  ];
};

/**
 * Extract script from Python tool input or output
 */
const extractScript = (data: unknown): string | null => {
  if (typeof data !== 'object' || data === null) return null;
  const obj = data as Record<string, unknown>;

  if (typeof obj.script === 'string') return obj.script;
  if (typeof obj.code === 'string') return obj.code;

  return null;
};

export const getPythonCustomActions = (ctx: {
  input: unknown;
  output: unknown;
  isCompleted: boolean;
}): CustomAction[] => {
  // Try input first (python-tool), then output (coder-tool streams script in output)
  const script = extractScript(ctx.input) || extractScript(ctx.output);

  if (!script) return [];

  return [
    {
      icon: createElement(Copy01Icon, { size: 11 }),
      label: 'Copy Script',
      onClick: () => {
        navigator.clipboard.writeText(script);
      },
    },
  ];
};

const InteractshOutputSchema = z.object({
  mode: z.literal('domain-generated'),
  url: z.string(),
  status: z.string().optional(),
  message: z.string().optional(),
});
const extractInteractshDomain = (data: unknown): string | null => {
  const result = InteractshOutputSchema.safeParse(data);

  return result.success ? result.data.url : null;
};

export const getInteractshCustomActions = (ctx: {
  input: unknown;
  output: unknown;
  isCompleted: boolean;
}): CustomAction[] => {
  const domain = extractInteractshDomain(ctx.output);

  if (!ctx.isCompleted || !domain) return [];

  return [
    {
      icon: createElement(RadioIcon, { size: 11 }),
      label: 'Open in Interact',
      onClick: () => {
        window.dispatchEvent(
          new CustomEvent('terra-open-interact-session', {
            detail: { domain },
            bubbles: true,
          }),
        );
      },
    },
  ];
};

// ============================================================================
// SSE Client custom actions
// ============================================================================
const SseConnectedOutputSchema = z.object({
  mode: z.literal('connected'),
  sessionId: z.string(),
  url: z.string(),
  status: z.string().optional(),
});
const SsePollOutputSchema = z.object({
  sessionId: z.string(),
  messages: z.array(z.unknown()).optional(),
  count: z.number().optional(),
  isActive: z.boolean().optional(),
});
const extractSseSessionInfo = (
  output: unknown,
): {
  sessionId: string;
  url: string;
  messages: unknown[];
  isActive: boolean;
} | null => {
  const connected = SseConnectedOutputSchema.safeParse(output);

  if (connected.success) {
    return {
      sessionId: connected.data.sessionId,
      url: connected.data.url,
      messages: [],
      isActive: true,
    };
  }

  const poll = SsePollOutputSchema.safeParse(output);

  if (poll.success && poll.data.sessionId) {
    // URL not in poll output — reconstruct what we can
    return {
      sessionId: poll.data.sessionId,
      url: '',
      messages: poll.data.messages ?? [],
      isActive: poll.data.isActive ?? false,
    };
  }

  return null;
};

const extractSseUrl = (input: unknown): string => {
  if (typeof input === 'object' && input !== null) {
    const obj = input as Record<string, unknown>;

    if (typeof obj.url === 'string') return obj.url;
  }

  return '';
};

export const getSseClientCustomActions = (ctx: {
  input: unknown;
  output: unknown;
  isCompleted: boolean;
}): CustomAction[] => {
  if (!ctx.isCompleted) return [];
  const info = extractSseSessionInfo(ctx.output);

  if (!info) return [];
  const url = info.url || extractSseUrl(ctx.input);

  return [
    {
      icon: createElement(Wifi01Icon, { size: 11 }),
      label: 'Open in Streaming',
      onClick: () => {
        window.dispatchEvent(
          new CustomEvent('terra-open-streaming-session', {
            detail: {
              sessionId: info.sessionId,
              type: 'sse',
              url,
              isActive: info.isActive,
              source: 'copilot',
              messages: info.messages,
            },
            bubbles: true,
          }),
        );
      },
    },
  ];
};

// ============================================================================
// WebSocket Client custom actions
// ============================================================================
const WsConnectedOutputSchema = z.object({
  mode: z.literal('connected'),
  sessionId: z.string(),
  url: z.string(),
  status: z.string().optional(),
});
const WsPollOutputSchema = z.object({
  sessionId: z.string(),
  messages: z.array(z.unknown()).optional(),
  count: z.number().optional(),
  isActive: z.boolean().optional(),
});
const extractWsSessionInfo = (
  output: unknown,
): {
  sessionId: string;
  url: string;
  messages: unknown[];
  isActive: boolean;
} | null => {
  const connected = WsConnectedOutputSchema.safeParse(output);

  if (connected.success) {
    return {
      sessionId: connected.data.sessionId,
      url: connected.data.url,
      messages: [],
      isActive: true,
    };
  }

  const poll = WsPollOutputSchema.safeParse(output);

  if (poll.success && poll.data.sessionId) {
    return {
      sessionId: poll.data.sessionId,
      url: '',
      messages: poll.data.messages ?? [],
      isActive: poll.data.isActive ?? false,
    };
  }

  return null;
};

const extractWsUrl = (input: unknown): string => {
  if (typeof input === 'object' && input !== null) {
    const obj = input as Record<string, unknown>;

    if (typeof obj.url === 'string') return obj.url;
  }

  return '';
};

export const getWebSocketClientCustomActions = (ctx: {
  input: unknown;
  output: unknown;
  isCompleted: boolean;
}): CustomAction[] => {
  if (!ctx.isCompleted) return [];
  const info = extractWsSessionInfo(ctx.output);

  if (!info) return [];
  const url = info.url || extractWsUrl(ctx.input);

  return [
    {
      icon: createElement(CableIcon, { size: 11 }),
      label: 'Open in Streaming',
      onClick: () => {
        window.dispatchEvent(
          new CustomEvent('terra-open-streaming-session', {
            detail: {
              sessionId: info.sessionId,
              type: 'websocket',
              url,
              isActive: info.isActive,
              source: 'copilot',
              messages: info.messages,
            },
            bubbles: true,
          }),
        );
      },
    },
  ];
};
